/**
 * @fileoverview Terms of Service page component.
 * @summary Displays terms of service for both organizers and singers.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/TermsPage
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */

import { useTranslation } from 'react-i18next'
import PageHeader from '../components/PageHeader'

/**
 * @summary Terms of Service page component.
 * @returns {React.ReactElement} Terms of service page.
 */
export default function TermsPage() {
  const { t } = useTranslation()

  return (
    <div className="min-h-screen bg-base-50 dark:bg-base-900">
      <PageHeader
        title={t('termsPage.title')}
        subtitle={t('termsPage.subtitle')}
      />

      <div className="page-container mx-auto py-4 px-4 bg-white dark:bg-gray-800 rounded-lg">
        <div className="prose dark:prose-invert max-w-none">
          {/* Last Updated */}
          <div className="mb-6 p-4 bg-base-100 dark:bg-base-800 rounded-lg border border-base-200 dark:border-base-700">
            <p className="text-sm text-(--color-text-secondary)">
              {t('termsPage.lastUpdated')}
            </p>
          </div>

          {/* Section 1: Introduction */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-(--color-text-primary) dark:text-white">
              {t('termsPage.intro.title')}
            </h2>
            <p className="text-(--color-text-secondary) mb-4">
              {t('termsPage.intro.description')}
            </p>
          </section>

          {/* Section 2: Service Description */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-(--color-text-primary) dark:text-white">
              {t('termsPage.service.title')}
            </h2>
            <p className="text-(--color-text-secondary) mb-4">
              {t('termsPage.service.description')}
            </p>

            <h3 className="text-xl font-semibold text-(--color-text-primary) dark:text-white mb-3 mt-4">
              {t('termsPage.service.organizers.title')}
            </h3>
            <p className="text-(--color-text-secondary) mb-4">
              {t('termsPage.service.organizers.description')}
            </p>

            <h3 className="text-xl font-semibold text-(--color-text-primary) dark:text-white mb-3 mt-4">
              {t('termsPage.service.singers.title')}
            </h3>
            <p className="text-(--color-text-secondary) mb-4">
              {t('termsPage.service.singers.description')}
            </p>
          </section>

          {/* Section 3: User Accounts */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-(--color-text-primary) dark:text-white">
              {t('termsPage.accounts.title')}
            </h2>
            <p className="text-(--color-text-secondary) mb-4">
              {t('termsPage.accounts.description')}
            </p>
            <ul className="list-disc list-inside space-y-2 text-(--color-text-secondary) mb-4">
              <li>{t('termsPage.accounts.responsibility')}</li>
              <li>{t('termsPage.accounts.accuracy')}</li>
              <li>{t('termsPage.accounts.security')}</li>
              <li>{t('termsPage.accounts.notTransferable')}</li>
            </ul>
          </section>

          {/* Section 4: Subscription and Payment */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-(--color-text-primary) dark:text-white">
              {t('termsPage.subscription.title')}
            </h2>
            <p className="text-(--color-text-secondary) mb-4">
              {t('termsPage.subscription.description')}
            </p>
            <ul className="list-disc list-inside space-y-2 text-(--color-text-secondary) mb-4">
              <li>{t('termsPage.subscription.free')}</li>
              <li>{t('termsPage.subscription.oneShot')}</li>
              <li>{t('termsPage.subscription.premium')}</li>
            </ul>
          </section>

          {/* Section 5: Payment Terms */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-(--color-text-primary) dark:text-white">
              {t('termsPage.payment.title')}
            </h2>
            <p className="text-(--color-text-secondary) mb-4">
              {t('termsPage.payment.description')}
            </p>
            <ul className="list-disc list-inside space-y-2 text-(--color-text-secondary) mb-4">
              <li>{t('termsPage.payment.currency')}</li>
              <li>{t('termsPage.payment.noRefund')}</li>
              <li>{t('termsPage.payment.cancellation')}</li>
              <li>{t('termsPage.payment.automaticRenewal')}</li>
            </ul>
          </section>

          {/* Section 6: User Conduct */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-(--color-text-primary) dark:text-white">
              {t('termsPage.conduct.title')}
            </h2>
            <p className="text-(--color-text-secondary) mb-4">
              {t('termsPage.conduct.description')}
            </p>
            <h3 className="text-xl font-semibold text-(--color-text-primary) dark:text-white mb-3 mt-4">
              {t('termsPage.conduct.prohibited.title')}
            </h3>
            <ul className="list-disc list-inside space-y-2 text-(--color-text-secondary) mb-4">
              <li>{t('termsPage.conduct.prohibited.illegal')}</li>
              <li>{t('termsPage.conduct.prohibited.harmful')}</li>
              <li>{t('termsPage.conduct.prohibited.harassing')}</li>
              <li>{t('termsPage.conduct.prohibited.spam')}</li>
              <li>{t('termsPage.conduct.prohibited.fraud')}</li>
            </ul>
          </section>

          {/* Section 7: Organizer Responsibilities */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-(--color-text-primary) dark:text-white">
              {t('termsPage.organizerResponsibilities.title')}
            </h2>
            <p className="text-(--color-text-secondary) mb-4">
              {t('termsPage.organizerResponsibilities.description')}
            </p>
            <ul className="list-disc list-inside space-y-2 text-(--color-text-secondary) mb-4">
              <li>{t('termsPage.organizerResponsibilities.songSelection')}</li>
              <li>{t('termsPage.organizerResponsibilities.eventManagement')}</li>
              <li>{t('termsPage.organizerResponsibilities.dataManagement')}</li>
              <li>{t('termsPage.organizerResponsibilities.copyrightCompliance')}</li>
            </ul>
          </section>

          {/* Section 8: Singer Rights and Responsibilities */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-(--color-text-primary) dark:text-white">
              {t('termsPage.singerRights.title')}
            </h2>
            <p className="text-(--color-text-secondary) mb-4">
              {t('termsPage.singerRights.description')}
            </p>
            <ul className="list-disc list-inside space-y-2 text-(--color-text-secondary) mb-4">
              <li>{t('termsPage.singerRights.participation')}</li>
              <li>{t('termsPage.singerRights.songSelection')}</li>
              <li>{t('termsPage.singerRights.noDuplication')}</li>
              <li>{t('termsPage.singerRights.respect')}</li>
            </ul>
          </section>

          {/* Section 9: Intellectual Property */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-(--color-text-primary) dark:text-white">
              {t('termsPage.intellectualProperty.title')}
            </h2>
            <p className="text-(--color-text-secondary) mb-4">
              {t('termsPage.intellectualProperty.description')}
            </p>
          </section>

          {/* Section 10: Limitation of Liability */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-(--color-text-primary) dark:text-white">
              {t('termsPage.liability.title')}
            </h2>
            <p className="text-(--color-text-secondary) mb-4">
              {t('termsPage.liability.description')}
            </p>
          </section>

          {/* Section 11: Termination */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-(--color-text-primary) dark:text-white">
              {t('termsPage.termination.title')}
            </h2>
            <p className="text-(--color-text-secondary) mb-4">
              {t('termsPage.termination.description')}
            </p>
          </section>

          {/* Section 12: Modifications */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-(--color-text-primary) dark:text-white">
              {t('termsPage.modifications.title')}
            </h2>
            <p className="text-(--color-text-secondary) mb-4">
              {t('termsPage.modifications.description')}
            </p>
          </section>

          {/* Section 13: Jurisdiction */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-(--color-text-primary) dark:text-white">
              {t('termsPage.jurisdiction.title')}
            </h2>
            <p className="text-(--color-text-secondary) mb-4">
              {t('termsPage.jurisdiction.description')}
            </p>
          </section>

          {/* Section 14: Contact */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-(--color-text-primary) dark:text-white">
              {t('termsPage.contact.title')}
            </h2>
            <p className="text-(--color-text-secondary) mb-2">
              {t('termsPage.contact.description')}
            </p>
            <p className="text-(--color-text-secondary)">
              <strong>{t('termsPage.contact.email')}</strong>
            </p>
          </section>
        </div>
      </div>
    </div>
  )
}

